/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { inject as service } from '@ember/service';
import { equal } from '@ember/object/computed';
import Component from '@glimmer/component';
import EmObject, { action, computed, set } from '@ember/object';
import moment, { Moment } from 'moment-timezone';
import { CREATED_IN_CIO_AT } from 'cio/utils/attributes';
import {
  arrayOperators,
  ComparisonOperator,
  dateExistenceOperators,
  dateInequalityOperators,
  dateOperators,
  emailOperators,
  equalityOperators,
  existenceOperators,
  extraDateInequalityOperators,
  inequalityOperators,
  stringOperators,
} from 'cio/utils/operators';
import AttributeCondition, {
  ago,
  canSelectComparisonOperator,
  fromNow,
  isAbsoluteTimestampOperator,
  isBinaryOperator,
  isRelativeTimestampOperator,
  isTwoValueOperator,
} from 'cio/app-models/conditions/attribute';
import EnvironmentService from 'cio/services/environment';
import EventAttributeCondition from 'cio/app-models/conditions/event-attribute';
import CollectionAttributeCondition from 'cio/app-models/conditions/collection-attribute';
import DeviceAttributeCondition from 'cio/app-models/conditions/device-attribute';
import FormEventAttributeCondition from 'cio/app-models/conditions/form-event-attribute';
import { JsonAttributeOperator, MAX_JSON_DEPTH } from 'cio/utils/conditions/json-attributes';
import { ConditionEditorSubLayout } from 'cio/utils/condition-editor';
import CampaignModel from 'cio/models/campaign';
import { tracked } from '@glimmer/tracking';
import { ModelType } from 'cio/utils/conversions';
import { dependentKeyCompat } from '@ember/object/compat';
import { findOperator } from 'cio/utils/conditions-operators';
import convertOperator from 'cio/utils/operators/convert';
import ObjectAttributeCondition, { ObjectAttributeSource } from 'cio/app-models/conditions/object-attribute';
import DS from 'ember-data';
import { ConditionType } from 'cio/utils/condition-types';

type KeyedOperatorGroupDropdownItem = {
  key: OperatorGroup;
  label: string;
  items: { label: string; value: string; disabled?: boolean }[];
};

export type OperatorGroupDropdownItem = {
  label: string;
  items: { label: string; value: string; disabled?: boolean }[];
};

export enum OperatorGroup {
  StringOrNumber = 'stringOrNumber',
  String = 'string',
  Email = 'email',
  UnixTimestamp = 'unixTimestamp',
  Array = 'array',
  JsonArray = 'jsonArray',
  JsonObject = 'jsonObject',
}

const OPERATOR_GROUP_LABELS: Record<OperatorGroup, string> = {
  [OperatorGroup.StringOrNumber]: 'String or Number',
  [OperatorGroup.String]: 'String',
  [OperatorGroup.Email]: 'Email',
  [OperatorGroup.UnixTimestamp]: 'Unix Timestamp',
  [OperatorGroup.Array]: 'Array',
  [OperatorGroup.JsonArray]: 'Array',
  [OperatorGroup.JsonObject]: 'Object',
};

const TIMESTAMP_OPERATORS_RELATIVE_TO_ABSOLUTE = {
  timestamp_lt: 'timestamp_lta',
  timestamp_gt: 'timestamp_gta',
};

const TIMESTAMP_OPERATORS_ABSOLUTE_TO_RELATIVE = {
  timestamp_lta: 'timestamp_lt',
  timestamp_gta: 'timestamp_gt',
};

function transformTimestampOperatorType(operator: string, toType: string) {
  if (toType === 'absolute' && operator in TIMESTAMP_OPERATORS_RELATIVE_TO_ABSOLUTE) {
    return TIMESTAMP_OPERATORS_RELATIVE_TO_ABSOLUTE[operator as keyof typeof TIMESTAMP_OPERATORS_RELATIVE_TO_ABSOLUTE];
  } else if (toType === 'relative' && operator in TIMESTAMP_OPERATORS_ABSOLUTE_TO_RELATIVE) {
    return TIMESTAMP_OPERATORS_ABSOLUTE_TO_RELATIVE[operator as keyof typeof TIMESTAMP_OPERATORS_ABSOLUTE_TO_RELATIVE];
  }

  return operator;
}

function mergeGroupsWithSameLabels(groups: KeyedOperatorGroupDropdownItem[]): OperatorGroupDropdownItem[] {
  let groupsByLabel = groups.reduce<Record<string, OperatorGroupDropdownItem[]>>((acc, group) => {
    if (acc[group.label] == null) {
      acc[group.label] = [];
    }

    acc[group.label].push(group);
    return acc;
  }, {});

  return groups
    .map((g) => g.label)
    .uniq()
    .map((label) => {
      let groups = groupsByLabel[label];
      return {
        label,
        items: groups.reduce((acc, g) => {
          return [...acc, ...g.items];
        }, []),
      };
    });
}

interface ConditionEditorAttributeArgs {
  attributeNames?: string[];

  condition:
    | AttributeCondition
    | EventAttributeCondition
    | CollectionAttributeCondition
    | DeviceAttributeCondition
    | FormEventAttributeCondition
    | ObjectAttributeCondition;

  allowedOperatorGroups?: OperatorGroup[];
  allowedOperators?: string[];

  allowArrayOperators?: boolean;
  allowJsonSupport?: boolean;
  showDateInequalityOperators?: boolean;

  itemClass?: string;
  size?: string;
  placeholder?: string;
  disabled: boolean;

  variation?: 'collection' | 'compact' | 'segment';
  subLayout?: ConditionEditorSubLayout;

  autosuggestValues?: boolean;
  autosuggestValueOverrides?: Record<string, string[] | { label: string; value: string }[]>;
  isLoadingAutosuggestValueOverrides?: boolean;

  campaign?: CampaignModel;
  modelType?: ModelType;

  onConditionChange?: () => unknown;
  onNameChanged?: () => unknown;
  onOperatorChanged?: () => unknown;
  onComparisonOperatorChanged?: () => unknown;
  onValueChanged?: () => unknown;
  onArrayConditionTypeChanged?: () => unknown;

  tagName?: string;

  isJsonChild?: boolean;

  objectTypeId?: number | string;
  isObject?: boolean;
}

export default class Attribute extends Component<ConditionEditorAttributeArgs> {
  @service environment!: EnvironmentService;
  @service store!: DS.Store;

  get tagName() {
    return this.args.tagName ?? '';
  }

  get disabled() {
    return this.args.disabled ?? false;
  }

  get autosuggestValues() {
    return this.args.autosuggestValues ?? true;
  }

  get autosuggestValueOverrides() {
    return this.args.autosuggestValueOverrides ?? {};
  }

  get isLoadingAutosuggestValueOverrides() {
    return this.args.isLoadingAutosuggestValueOverrides ?? false;
  }

  @dependentKeyCompat
  get allowArrayOperators() {
    return this.args.allowArrayOperators ?? false;
  }

  get allowJsonSupport() {
    let allowJsonSupport = this.args.allowJsonSupport ?? false;
    let { condition } = this.args;

    return allowJsonSupport && condition.jsonDepth < MAX_JSON_DEPTH;
  }

  @dependentKeyCompat
  get showDateInequalityOperators() {
    return this.args.showDateInequalityOperators ?? true;
  }

  get attributeNames() {
    let { objectTypeId, condition } = this.args;
    let fallbackAttributes = [...this.environment.sentAttributes];
    if (objectTypeId != null) {
      let objectType = this.store.peekRecord('object-type', objectTypeId);
      fallbackAttributes = objectType?.sentAttributes ?? [];

      if (
        (condition as ObjectAttributeCondition)?.objectAttributeSource ===
          ObjectAttributeSource.RelationshipAttribute ||
        condition.type === ConditionType.RelationshipAttribute
      ) {
        fallbackAttributes = objectType?.sentRelAttributes ?? [];
      }
    }

    return this.args.attributeNames ?? fallbackAttributes;
  }

  get isJsonChild() {
    return this.args.isJsonChild ?? false;
  }

  @computed('args.condition.{type,editableCondition}')
  get editableCondition():
    | AttributeCondition
    | EventAttributeCondition
    | CollectionAttributeCondition
    | DeviceAttributeCondition
    | FormEventAttributeCondition
    | ObjectAttributeCondition {
    let { condition } = this.args;

    if (!['attribute', 'event_attribute', 'collection_attribute', 'object_attribute'].includes(condition.type)) {
      return condition;
    }

    if (condition.editableCondition != null) {
      return condition.editableCondition;
    }

    return condition;
  }

  @computed('args.condition.operator')
  get operatorHasPredicate() {
    let { condition } = this.args;

    if (condition?.operator == null) {
      return false;
    }

    return findOperator(condition.operator)?.hasPredicate ?? false;
  }

  @tracked errorMessage = '';

  @computed('showDateInequalityOperators', 'editableCondition.operator')
  get hasUnsupportedCondition() {
    if (this.showDateInequalityOperators) {
      return false;
    }

    let conditionOperator = this.editableCondition.operator;
    return dateInequalityOperators.some((operator) => operator.value === conditionOperator);
  }

  @computed('hasUnsupportedCondition', 'editableCondition.{name,errors.operator,operator}')
  get hasError() {
    let condition = this.editableCondition;
    if (this.hasUnsupportedCondition) {
      this.errorMessage = 'This condition is no longer supported';
      return true;
    }

    let { operator } = condition;
    if (condition.name !== 'email' && (operator === 'email_valid' || operator === '!email_valid')) {
      this.errorMessage = 'This operator can only be used for the email attribute';
      return true;
    }

    if (condition.errors.operator?.length) {
      this.errorMessage = condition.errors.operator?.[0];
      return true;
    }

    return false;
  }

  @computed('editableCondition.operator')
  get showRelativeTimestampValue() {
    return isRelativeTimestampOperator(this.editableCondition.operator);
  }

  @computed('editableCondition.operator')
  get showValue() {
    return isBinaryOperator(this.editableCondition.operator);
  }

  @computed('editableCondition.operator')
  get showTwoValues() {
    return isTwoValueOperator(this.editableCondition.operator);
  }

  @equal('editableCondition.operator', 'timestamp_bw') isTwoValuesTimestamp!: boolean;

  @computed('editableCondition.operator')
  get isTimestamp() {
    return (
      isAbsoluteTimestampOperator(this.editableCondition.operator) ||
      isRelativeTimestampOperator(this.editableCondition.operator)
    );
  }

  @dependentKeyCompat
  get allowedOperatorGroups(): OperatorGroup[] {
    if (this.args.allowedOperatorGroups != null) {
      return this.args.allowedOperatorGroups;
    }

    let groups = [OperatorGroup.StringOrNumber, OperatorGroup.String, OperatorGroup.Email, OperatorGroup.UnixTimestamp];

    if (this.allowArrayOperators) {
      groups.push(OperatorGroup.Array);
    }

    if (this.allowJsonSupport && !(this.args.condition.isAdvancedJsonMode ?? false)) {
      groups.push(OperatorGroup.JsonArray, OperatorGroup.JsonObject);
    }

    return groups;
  }

  @tracked selectedTimestampsDates: any = null;

  timeDirections = [
    { value: ago, label: 'ago' },
    { value: fromNow, label: 'from now' },
  ];

  timestampInequalityOperatorVariants = [
    { value: 'relative', label: 'a relative date' },
    { value: 'absolute', label: 'a specific date' },
  ];

  @tracked selectedTimestampInequalityOperatorVariant = '';

  @computed('editableCondition.name')
  private get isAttrCreatedInCIOAt() {
    return this.editableCondition.name === CREATED_IN_CIO_AT;
  }

  @computed('editableCondition.operator')
  get selectedOperatorDropdownOption() {
    let { operator } = this.editableCondition;

    // only relative timestamp operator values are displayed in the dropdown, despite representing both absolute/relative variants
    // normalize the value to ensure correct option is showing as selected
    return transformTimestampOperatorType(operator, 'relative');
  }

  @computed('editableCondition.operator')
  get canCompareValues() {
    if (this.editableCondition.operator == null) {
      return false;
    }

    return canSelectComparisonOperator(convertOperator(this.editableCondition.operator));
  }

  constructor(owner: unknown, args: ConditionEditorAttributeArgs) {
    super(owner, args);

    this.selectedTimestampsDates = EmObject.create({
      value:
        this.isTimestamp || this.isTwoValuesTimestamp
          ? args.condition.value != null
            ? moment.unix(Number(args.condition.value))
            : null
          : null,
      value2:
        this.isTimestamp || this.isTwoValuesTimestamp
          ? args.condition.value2 != null
            ? moment.unix(Number(args.condition.value2))
            : null
          : null,
    });

    this.selectedTimestampInequalityOperatorVariant = isAbsoluteTimestampOperator(args.condition.operator)
      ? 'absolute'
      : 'relative';

    if (!this.isJsonChild && this.allowJsonSupport) {
      this.args.condition.createEditCopy();
    }
  }

  // computes operator group items
  @computed(
    'editableCondition.{name,operator,comparisonOperator}',
    'showDateInequalityOperators',
    'hasUnsupportedCondition',
    'allowArrayOperators',
    'allowedOperatorGroups',
    'args.{allowedOperators,condition.editableCondition.objectAttributeSource}',
  )
  get operatorGroups() {
    let condition = this.editableCondition;
    let stringOrNumberGroup = {
      key: OperatorGroup.StringOrNumber,
      label: OPERATOR_GROUP_LABELS[OperatorGroup.StringOrNumber],
      items: [
        ...[...equalityOperators, ...inequalityOperators].map((operator) => ({
          ...operator,
          disabled: this.isDisabledComparisonOperator(operator.value) || this.isAttrCreatedInCIOAt,
        })),
        ...existenceOperators.map((operator) => ({
          ...operator,
          disabled: this.isAttrCreatedInCIOAt,
        })),
      ],
    };
    let stringGroup = {
      key: OperatorGroup.String,
      label: OPERATOR_GROUP_LABELS[OperatorGroup.String],
      items: stringOperators.map((operator) => ({
        ...operator,
        disabled: this.isAttrCreatedInCIOAt,
      })),
    };
    let arrayGroup = {
      key: OperatorGroup.Array,
      label: OPERATOR_GROUP_LABELS[OperatorGroup.Array],
      items: arrayOperators.map((operator) => ({
        ...operator,
        disabled: this.isAttrCreatedInCIOAt,
      })),
    };
    let jsonArrayGroup = {
      key: OperatorGroup.JsonArray,
      label: OPERATOR_GROUP_LABELS[OperatorGroup.JsonArray],
      items: [
        {
          label: 'where at least one',
          value: JsonAttributeOperator.ArrayOrObjectHasPropertyOrValue,
          disabled: this.isAttrCreatedInCIOAt,
        },
      ],
    };
    let jsonObjectGroup = {
      key: OperatorGroup.JsonObject,
      label: OPERATOR_GROUP_LABELS[OperatorGroup.JsonObject],
      items: [
        {
          label: 'has the property',
          value: JsonAttributeOperator.ObjectHasProperty,
          disabled: this.isAttrCreatedInCIOAt,
        },
      ],
    };
    let emailGroup = {
      key: OperatorGroup.Email,
      label: OPERATOR_GROUP_LABELS[OperatorGroup.Email],
      items: emailOperators.map((operator) => ({ ...operator, disabled: condition.name !== 'email' })),
    };

    let groups: KeyedOperatorGroupDropdownItem[] = [
      stringOrNumberGroup,
      stringGroup,
      arrayGroup,
      jsonArrayGroup,
      jsonObjectGroup,
      emailGroup,
    ];

    if (this.showDateInequalityOperators) {
      let unixTimestampGroup = {
        key: OperatorGroup.UnixTimestamp,
        label: OPERATOR_GROUP_LABELS[OperatorGroup.UnixTimestamp],
        // exclude absolute timestamp operators: the ability to switch between relative and absolute timestamps is handled by another dropdown
        items: dateOperators
          .filter((o) => !['timestamp_lta', 'timestamp_gta'].includes(o.value))
          .map((operator) => ({
            ...operator,
            disabled:
              this.isDisabledComparisonOperator(operator.value) ||
              (['timestamp', '!timestamp'].includes(operator.value) && this.isAttrCreatedInCIOAt) ||
              condition.disallowDateOperators,
          })),
      };

      groups.push(unixTimestampGroup);
    } else {
      let unixTimestampGroup: KeyedOperatorGroupDropdownItem = {
        key: OperatorGroup.UnixTimestamp,
        label: OPERATOR_GROUP_LABELS[OperatorGroup.UnixTimestamp],
        items: [...dateExistenceOperators],
      };

      if (this.hasUnsupportedCondition) {
        let conditionOperator = condition.operator;
        let disabledInequalityOperators = dateInequalityOperators
          .filter((operator) => operator.value === conditionOperator)
          .map((operator) => ({
            ...operator,
            disabled: true,
          }));

        unixTimestampGroup.items.push(...disabledInequalityOperators);
      }

      unixTimestampGroup.items.push(...extraDateInequalityOperators);
      unixTimestampGroup.items = unixTimestampGroup.items.map((operator) => ({
        ...operator,
        disabled: (operator.disabled ?? false) || condition.disallowDateOperators,
      }));

      groups.push(unixTimestampGroup);
    }

    groups = groups.filter((group) => this.allowedOperatorGroups?.includes(group.key));

    if (this.args.allowedOperators != null) {
      groups = groups.map((group) => ({
        ...group,
        items: group.items.filter((operator) => this.args.allowedOperators!.includes(operator.value)),
      }));
    }

    let mergedGroups = mergeGroupsWithSameLabels(groups);
    mergedGroups = mergedGroups.filter((group) => group.items.length > 0);
    return mergedGroups;
  }

  @computed('editableCondition.childCondition', 'args.condition.isAdvancedJsonMode')
  get shouldShowNestedEditor() {
    return this.editableCondition.childCondition != null && !this.args.condition.isAdvancedJsonMode;
  }

  // any changes within the datepicker: update local selectedTimestampsDates object
  @action
  onTimestampValueSelect(valueKey: 'value' | 'value2', { date }: { date: Moment }) {
    this.selectedTimestampsDates.set(valueKey, date);
    set(this.editableCondition, valueKey, String(date.unix()));

    this.onConditionChange();
  }

  // called after datepicker is closed: convert selected datetime to local timezone, set unix timestamp on condition object
  @action
  timestampValueSelectionFinished(valueKey: 'value' | 'value2') {
    if (this.selectedTimestampsDates[valueKey] == null) {
      return;
    }

    let selectedTimestamp = this.selectedTimestampsDates[valueKey].unix();

    this.selectedTimestampsDates.set(valueKey, moment.unix(selectedTimestamp));
    set(this.editableCondition, valueKey, String(selectedTimestamp));

    this.onConditionChange();
  }

  @action
  timestampInequalityVariantChange(value: string) {
    let condition = this.editableCondition;

    set(condition, 'operator', transformTimestampOperatorType(condition.operator, value));

    this.onConditionChange();
  }

  @action
  onOperatorSelect(operator: string) {
    let condition = this.editableCondition;

    // if already edited a timestamp condition: properly transform operator type to proper selected variant ("lta/gta" or "lt/gt")
    let newOperator = this.selectedTimestampInequalityOperatorVariant
      ? transformTimestampOperatorType(operator, this.selectedTimestampInequalityOperatorVariant)
      : operator;

    set(condition, 'operator', newOperator);

    // safe-check to make sure timestamp condition relative/absolute variant is always selected
    if (this.isTimestamp) {
      this.selectedTimestampInequalityOperatorVariant = isAbsoluteTimestampOperator(newOperator)
        ? 'absolute'
        : 'relative';
    }

    if (canSelectComparisonOperator(newOperator)) {
      if (!condition.comparisonOperator) {
        set(condition, 'comparisonOperator', ComparisonOperator.Value);
      }
    } else {
      // When switching away from comparison operators ("customer's profile
      // attribute", "event attribute"), make sure we reset the
      // comparisonOperator field on the condition back to the default
      if (condition.comparisonOperator !== ComparisonOperator.Value) {
        set(condition, 'comparisonOperator', '');
      }
    }

    this.onConditionChange();
    this.args.onOperatorChanged?.();
  }

  @action
  onNameChange() {
    this.onConditionChange();
    this.args.onNameChanged?.();
  }

  @action
  onValueChange() {
    this.onConditionChange();
    this.args.onValueChanged?.();
  }

  @action
  onComparisonOperatorSelect(operator: ComparisonOperator) {
    let condition = this.editableCondition;

    let oldOperator = condition.comparisonOperator as ComparisonOperator;
    set(condition, 'comparisonOperator', operator);

    // When switching between profile and event attribute comparisons, clear the value field
    let nonValueOperators = [
      ComparisonOperator.Customer,
      ComparisonOperator.Event,
      ComparisonOperator.ForeignEventAttribute,
    ];
    if (oldOperator !== operator && nonValueOperators.includes(oldOperator) && nonValueOperators.includes(operator)) {
      set(condition, 'value', '');
    }

    this.onConditionChange();
    this.args.onComparisonOperatorChanged?.();
  }

  private isDisabledComparisonOperator(value: string): boolean {
    let condition = this.editableCondition;
    // exclude is-between operator for certain comparison operators
    let disableInBetweenForComparisonOperators = [
      ComparisonOperator.Customer,
      ComparisonOperator.Event,
      ComparisonOperator.Value,
    ];
    let disableTimestampValues = ['timestamp_bw', 'bw'];
    if (condition instanceof EventAttributeCondition) {
      // we disable these operators on conversionEventAttributes only until BE supports them
      if (condition.isConversionEventAttribute) {
        return (
          disableInBetweenForComparisonOperators.includes(condition.comparisonOperator as ComparisonOperator) &&
          disableTimestampValues.includes(value)
        );
      }
    }
    return false;
  }

  @action
  onArrayConditionTypeChange() {
    this.onConditionChange();
    this.args.onArrayConditionTypeChanged?.();
  }

  @action
  onConditionChange() {
    this.editableCondition.applyUpdates();

    this.args.onConditionChange?.();
  }
}
